/**
 * Global shared types — UserProfile, Subscription, and cross-cutting enums.
 */

// ─── Subscription ───────────────────────────────────────────────────────────

/**
 * PlanTier is the display/gate tier (free | pro | enterprise).
 * For Firestore storage the full plan string (pro_monthly / pro_annual) is used
 * via SubscriptionPlan below.
 */
export type PlanTier = "free" | "pro" | "enterprise";

/** Exact plan values stored in Firestore — mirrors reactjs/src/lib/userProfile.ts */
export type SubscriptionPlan = "free" | "pro_monthly" | "pro_annual" | "enterprise";

/** Derived helper: map a SubscriptionPlan to its display PlanTier */
export function planToTier(plan: SubscriptionPlan): PlanTier {
  if (plan === "pro_monthly" || plan === "pro_annual") return "pro";
  if (plan === "enterprise") return "enterprise";
  return "free";
}

/** Per-conversation token ceiling for free vs. paid accounts. */
export const FREE_TOKEN_LIMIT = 30_000;
export const PAID_TOKEN_LIMIT = 300_000;

/**
 * True when the account has paid access (not free).
 *
 * Tolerant by design: any tier that is "pro"/"enterprise" OR any non-empty plan
 * that is not literally "free" counts as paid, regardless of the exact string.
 * This is the single source of truth for Pro gates (session limits, token
 * limits, file-count limits, hard deletes) — it must never depend on a fixed
 * list of plan IDs, otherwise a paid user can fall through to free-tier rules.
 */
export function isPaidProfile(
  sub?: { plan?: string | null; tier?: string | null } | null
): boolean {
  if (!sub) return false;
  const plan = sub.plan as SubscriptionPlan | undefined;
  const tier = sub.tier as PlanTier | undefined;
  if (tier === "pro" || tier === "enterprise") return true;
  return !!plan && plan !== "free";
}

/**
 * Per-conversation token limit for a plan string.
 * Any non-free plan gets the paid ceiling.
 */
export function tokenLimitForPlan(plan?: string | null): number {
  return plan && plan !== "free" ? PAID_TOKEN_LIMIT : FREE_TOKEN_LIMIT;
}

export interface Subscription {
  /** Full Firestore plan value */
  plan: SubscriptionPlan;
  /** Convenience tier for gating UI */
  tier: PlanTier;
  status: "active" | "expired" | "cancelled" | "past_due";
  /** ISO date string */
  startDate?: string;
  /** ISO date string */
  endDate?: string;
  /** ISO date string — when the current billing period ends (Stripe) */
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  /** Stripe price ID of the active subscription */
  priceId?: string;
  /** PayPal subscription ID (if paid via PayPal) */
  paypalSubscriptionId?: string;
}
// ─── User ────────────────────────────────────────────────────────────────────

export interface UserProfile {
  id: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  /** Firebase UID */
  uid: string;
  subscription: Subscription;
  /** ISO date string */
  createdAt: string;
  /** ISO date string */
  updatedAt: string;
  /** MFA is enrolled */
  mfaEnabled?: boolean;
  /**
   * Persistent free-tier allowance flag for RFP Packs. Set once when a free
   * user generates their first pack. Never cleared on pack deletion — the
   * allowance is consumed for life, not per current pack count.
   */
  freePackUsed?: boolean;
  /**
   * Persistent free-tier allowance flag for Supplier Contract Reviews. Set
   * once when a free user generates their first review. Never cleared on
   * review deletion — the allowance is consumed for life.
   */
  freeContractUsed?: boolean;
  /** Marks this account as a demo account */
  isDemo?: boolean;
  /** Marks this account as a student account */
  isStudent?: boolean;
  /** User's preferred role — used for demo/role-preview routes */
  role?: "evaluator" | "observer" | "approver" | "supplier" | "admin";
  /** System-level role used for Firestore security rules */
  systemRole?: "customer" | "superadmin";
  /**
   * Soft-delete: set when the user requests account deletion.
   * ISO date string. Account is hidden/blocked but data is retained until manual purge.
   */
  deleteRequestedAt?: string;
  /**
   * The hostname where this account was originally created (e.g. "nh.scmsensei.ai").
   * Users are redirected to this domain on every login/session start.
   * Defaults to "www.scmsensei.ai" when absent (existing accounts).
   */
  signup_domain?: string;
}

// ─── API ─────────────────────────────────────────────────────────────────────

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}
